import { middleware } from '@seek/metrics-js';
import { v4 as createGuid } from 'uuid';

import type { AppConfig } from 'src/modules/appConfig/types';
import type { ApiClient } from 'src/types';

import { middlewareErrorLogger } from '../modules/error-logger';

import { createCareerHistoryAnalyticsMiddleware } from './analytics/careerHistory/createCareerHistoryAnalyticsMiddleware';
import { createCertsyAnalyticsMiddleware } from './analytics/certsy/createCertsyAnalyticsMiddleware';
import { createEducationAnalyticsMiddleware } from './analytics/education/createEducationAnalyticsMiddleware';
import { createLanguagesAnalyticsMiddleware } from './analytics/languages/createLanguagesAnalyticsMiddleware';
import { createLicencesAnalyticsMiddleware } from './analytics/licences/createLicencesAnalyticsMiddleware';
import { createNextRoleAnalyticsMiddleware } from './analytics/nextRole/createNextRoleAnalyticsMiddleware';
import { createPersonalSummaryAnalyticsMiddleware } from './analytics/personalSummary/createPersonalSummaryAnalyticsMiddleware';
import { createResumeAnalyticsMiddleware } from './analytics/resume/createResumeAnalyticsMiddleware';
import { createSkillsAnalyticsMiddleware } from './analytics/skills/createSkillsAnalyticsMiddleware';
import { createYearsOfExperienceAnalyticsMiddleware } from './analytics/yearsOfExperience/createYearsOfExperienceAnalyticsMiddleware';
import {
  createAnalyticsMiddleware,
  type Analytics,
} from './createAnalyticsMiddleware';
// @ts-expect-error
import { createBlendMiddleware } from './createBlendMiddleware';
import { createHistoryMiddleware } from './createHistoryMiddleware';
import { createHotjarMiddleware } from './createHotjarMiddleware';
import { createImpressionTrackingMiddleware } from './createImpressionTrackingMiddleware';
import { createLoadingMiddleware } from './createLoadingMiddleware';
import { createMetricsMiddleware } from './createMetricsMiddleware';
import { createRouteMiddleware } from './createRouteMiddleware';
// @ts-expect-error
import { createSyncingMiddleware } from './createSyncingMiddleware';
import { createDependencyInjector } from './dependencyInjector';

export const createMiddlewareList = ({
  analytics,
  apiClient,
  appConfig,
}: {
  analytics: Analytics;
  apiClient: ApiClient;
  appConfig: AppConfig;
}) => {
  const analyticsMiddlewares = [
    createAnalyticsMiddleware(analytics, apiClient, appConfig),
    createSkillsAnalyticsMiddleware(analytics, apiClient, appConfig),
    createNextRoleAnalyticsMiddleware(analytics, apiClient, appConfig),
    createEducationAnalyticsMiddleware(analytics, apiClient, appConfig),
    createLanguagesAnalyticsMiddleware(analytics),
    createLicencesAnalyticsMiddleware(analytics),
    createCareerHistoryAnalyticsMiddleware(analytics, apiClient, appConfig),
    createPersonalSummaryAnalyticsMiddleware(analytics, apiClient),
    createResumeAnalyticsMiddleware(analytics, apiClient),
    createCertsyAnalyticsMiddleware(analytics),
    createYearsOfExperienceAnalyticsMiddleware(analytics),
  ];

  return [
    createDependencyInjector({ api: apiClient, createGuid, appConfig }),
    createBlendMiddleware({ api: apiClient, appConfig }),
    // Note: the route middleware should always be before the history middleware to avoid routing bugs https://github.com/SEEK-Jobs/ca-myprofile-ui/pull/2886
    createRouteMiddleware({ api: apiClient, appConfig }),
    createHistoryMiddleware(history, appConfig),
    middlewareErrorLogger(),
    ...analyticsMiddlewares,
    createImpressionTrackingMiddleware({ api: apiClient }),
    middleware.reduxActionMetrics({}),
    createSyncingMiddleware(),
    createMetricsMiddleware(apiClient, appConfig),
    createHotjarMiddleware(),
    createLoadingMiddleware(),
  ];
};
